import { BASE_API_URL } from "../../../../config/constants";
import { MINIMAL_PAGINATION_NUM } from "../../../../config/pagination";
import { CloseShiftViolationRequestBody } from "../../../types/alerts";
import { IGetUserProfileResponse } from "../../../types/role-management";
import {
  AddCategory,
  AddKPI,
  AddServiceReqeustBody,
  AlertsRequest,
  AlertsResponse,
  CategoriesResponse,
  CustomInspectionPayload,
  CustomInspectionRequestBody,
  DefaultPricingPreference,
  DeleteCategoryRequest,
  DepotKPI,
  EditCustomInspectionRequestBody,
  GetRepairService,
  GetRepairServiceResponse,
  ICreateCategoryRes,
  MakeCategoryParentRequest,
  OxAppReleaseResponse,
  PasswordTypes,
  PaymentPreferences,
  ProfileTypes,
  SingleAlert,
  SingleServiceResponse,
  UpdateCategoryRequest,
  VehicleTypesInterface
} from "../../../types/settings";
import {
  ApiResponseMetadata,
  GenericResponse,
  V3APIResponseMetadata
} from "../../../types/shared";
import { ALERT_STATUS } from "../../../types/shared/enums";
import { ISingleRepairLog } from "../../../types/trucksTypes";
import { baseAPI } from "../../api";

/**
 * SETTINGS ENDPOINTS
 * @author Elie K. Gashagaza
 * @authorEmail gashagaza@awesomity.rw
 * @since Jul 2022
 */

const settingsApi = baseAPI.injectEndpoints({
  endpoints: (builder) => ({
    /** Personal Information Requests */
    getUserProfile: builder.query<
      ApiResponseMetadata<IGetUserProfileResponse>,
      void
    >({
      providesTags: ["UserProfile"],
      query: () => ({
        url: "/users/profile",
        method: "GET"
      })
    }),

    personalInfo: builder.mutation<GenericResponse, ProfileTypes>({
      invalidatesTags: ["UserProfile"],
      query: (DTO) => ({
        url: "/users/profile",
        method: "PATCH",
        body: DTO
      })
    }),

    changePassword: builder.mutation<GenericResponse, PasswordTypes>({
      invalidatesTags: ["UserProfile"],
      query: (DTO) => ({
        url: "/users/profile",
        method: "PATCH",
        body: DTO
      })
    }),

    /** KPIs Requests */
    getKPIs: builder.query<ApiResponseMetadata<DepotKPI[]>, void>({
      providesTags: ["GetKPIs"],
      query: () => ({
        url: "/kpis/current",
        method: "GET"
      })
    }),
    addKpi: builder.mutation<ApiResponseMetadata<DepotKPI>, AddKPI>({
      invalidatesTags: ["GetKPIs"],
      query: (DTO) => ({
        url: "/kpis",
        method: "POST",
        body: DTO
      })
    }),

    getRepairServices: builder.query<
      V3APIResponseMetadata<GetRepairServiceResponse>,
      GetRepairService
    >({
      providesTags: ["GetRepairService"],
      query: ({ page, size, query }) => ({
        params: {
          page: page ?? 0,
          size: size ?? MINIMAL_PAGINATION_NUM,
          q: query ?? ""
        },
        url: `/repair-services`,
        method: "GET"
      })
    }),
    addCategory: builder.mutation<
      ApiResponseMetadata<ICreateCategoryRes>,
      AddCategory
    >({
      invalidatesTags: ["GetCategories", "Categories", "StockItems"],
      query: (DTO) => ({
        url: "/categories",
        method: "POST",
        body: DTO
      })
    }),

    addRepairService: builder.mutation<
      ApiResponseMetadata<SingleServiceResponse>,
      AddServiceReqeustBody
    >({
      invalidatesTags: ["GetRepairService"],
      query: (DTO) => ({
        url: "/repair-services",
        method: "POST",
        body: DTO
      })
    }),

    deleteCategory: builder.mutation<
      ApiResponseMetadata<CategoriesResponse>,
      DeleteCategoryRequest
    >({
      invalidatesTags: ["GetCategories", "Categories", "StockItems"],
      query: (DTO) => ({
        params: { alternativeCategory: DTO.alternativeCategory },
        url: `/categories/${DTO?.id}`,
        method: "DELETE",
        body: DTO
      })
    }),

    deleteRepairService: builder.mutation<
      ApiResponseMetadata<CategoriesResponse>,
      DeleteCategoryRequest
    >({
      invalidatesTags: ["GetRepairService"],
      query: (DTO) => ({
        url: `/repair-services/${DTO?.id}`,
        method: "DELETE",
        body: DTO
      })
    }),
    updateCategory: builder.mutation<
      ApiResponseMetadata<CategoriesResponse>,
      UpdateCategoryRequest
    >({
      invalidatesTags: ["GetCategories", "Categories", "StockItems"],
      query: (DTO) => ({
        url: `/categories/${DTO?.id}`,
        method: "PUT",
        body: DTO
      })
    }),

    updateRepairService: builder.mutation<
      ApiResponseMetadata<CategoriesResponse>,
      AddServiceReqeustBody
    >({
      invalidatesTags: ["GetRepairService"],
      query: (DTO) => ({
        url: `/repair-services/${DTO?.id}`,
        method: "PATCH",
        body: DTO
      })
    }),

    updateRepairLog: builder.mutation<
      ApiResponseMetadata<CategoriesResponse>,
      ISingleRepairLog
    >({
      invalidatesTags: ["GetRepairLog", "GetSpareParts"],
      query: (DTO) => ({
        url: `/repairs/${DTO.id}`,
        method: "PATCH",
        body: DTO
      })
    }),

    updatePricingPreference: builder.mutation<
      DefaultPricingPreference,
      { kgkm: number }
    >({
      invalidatesTags: [],
      query: (DTO) => ({
        url: `/depots/default-pricing-preference`,
        method: "PATCH",
        body: DTO
      })
    }),

    makeCategoryParent: builder.mutation<
      ApiResponseMetadata<CategoriesResponse>,
      MakeCategoryParentRequest
    >({
      invalidatesTags: ["GetCategories", "Categories"],
      query: (DTO) => ({
        url: `/categories/${DTO?.id}/make-parent`,
        method: "PUT"
      })
    }),

    oxAppRelease: builder.query<
      ApiResponseMetadata<OxAppReleaseResponse>,
      void
    >({
      providesTags: ["oxAppRelease"],
      query: () => ({
        url: `${BASE_API_URL?.split("/api/v1")[0]}/app/releases/latest`,
        method: "GET"
      })
    }),

    getShiftPreferences: builder.query<
      ApiResponseMetadata<{ maxHoursPerDay: number }>,
      void
    >({
      providesTags: [],
      query: () => ({
        url: `/shift-preferences/current`,
        method: "GET"
      })
    }),

    getPricingPreference: builder.query<DefaultPricingPreference, void>({
      providesTags: [],
      query: () => ({
        url: `/depots/default-pricing-preference`,
        method: "GET"
      })
    }),

    addShiftPreferences: builder.mutation<
      ApiResponseMetadata<CategoriesResponse>,
      AddCategory
    >({
      invalidatesTags: [],
      query: (DTO) => ({
        url: `/shift-preferences`,
        method: "POST",
        body: DTO
      })
    }),

    addPaymentPreferences: builder.mutation<
      ApiResponseMetadata<PaymentPreferences>,
      PaymentPreferences
    >({
      invalidatesTags: ["MoMoPaymentHaltStatus"],
      query: (DTO) => ({
        url: `/momo-payments/halt-momo-payment`,
        method: "PATCH",
        body: DTO
      })
    }),

    getPaymentPreferences: builder.query<{ payload: PaymentPreferences }, any>({
      providesTags: ["MoMoPaymentHaltStatus"],
      query: () => ({
        url: `/momo-payments/momo-payment-halt-status`,
        method: "GET"
      })
    }),

    addVehicleCategory: builder.mutation<
      ApiResponseMetadata<PaymentPreferences>,
      void
    >({
      invalidatesTags: ["GetVehicleType"],
      query: (DTO) => ({
        url: `/vehicle-types`,
        method: "POST",
        body: DTO
      })
    }),

    editVehicleCategory: builder.mutation<
      ApiResponseMetadata<CustomInspectionPayload[]>,
      { id: number | string }
    >({
      invalidatesTags: ["GetVehicleType"],
      query: (DTO) => ({
        url: `/vehicle-types/${DTO.id || ""}`,
        method: "PATCH",
        body: DTO
      })
    }),

    getVehicleCategory: builder.query<
      { payload: VehicleTypesInterface[] },
      void
    >({
      providesTags: ["GetVehicleType"],
      query: () => ({
        url: `/vehicle-types/count-check-items`,
        method: "GET"
      })
    }),

    getAlerts: builder.query<AlertsResponse, AlertsRequest>({
      providesTags: ["GetAlerts"],
      query: ({ page = 0, size = 10, type, depotId, truckId, status }) => ({
        params: {
          page,
          size,
          type: type || "",
          depotId: depotId || "",
          truckId: truckId || "",
          status: status || ""
        },
        url: `/alerts`,
        method: "GET"
      })
    }),

    changeAlertStatus: builder.mutation<
      { payload: SingleAlert },
      { id: number; status: keyof typeof ALERT_STATUS }
    >({
      invalidatesTags: [],
      query: (DTO) => ({
        url: `/alerts/${DTO.id}/status`,
        method: "PATCH",
        body: DTO
      })
    }),

    closeShiftViolation: builder.mutation<
      { payload: SingleAlert },
      CloseShiftViolationRequestBody
    >({
      invalidatesTags: ["GetAlerts"],
      query: (DTO) => ({
        url: `/alerts/${DTO.alertId}/close-shift-violation`,
        method: "PATCH",
        body: DTO
      })
    }),

    getCustomInspections: builder.query<
      ApiResponseMetadata<CustomInspectionPayload[]>,
      { vehicleTypeId?: number }
    >({
      providesTags: ["GetCustomInspection"],
      query: ({ vehicleTypeId = "" }) => ({
        params: { vehicleTypeId: vehicleTypeId || "" },
        url: `/inspections`,
        method: "GET"
      })
    }),

    deleteCustomInspection: builder.mutation<
      ApiResponseMetadata<CustomInspectionPayload[]>,
      { id: number }
    >({
      invalidatesTags: ["GetCustomInspection"],
      query: ({ id }) => ({
        url: `/inspections/${id}`,
        method: "DELETE"
      })
    }),

    addCustomInspection: builder.mutation<
      ApiResponseMetadata<CustomInspectionPayload[]>,
      CustomInspectionRequestBody
    >({
      invalidatesTags: ["GetCustomInspection"],
      query: (DTO) => ({
        url: `/inspections`,
        method: "POST",
        body: DTO
      })
    }),

    editCustomInspection: builder.mutation<
      ApiResponseMetadata<CustomInspectionPayload[]>,
      EditCustomInspectionRequestBody
    >({
      invalidatesTags: ["GetCustomInspection"],
      query: (DTO) => ({
        url: `/inspections/${DTO.inspectionId}`,
        method: "PUT",
        body: DTO
      })
    })
  })
});

export const {
  useGetUserProfileQuery,
  usePersonalInfoMutation,
  useChangePasswordMutation,
  useGetKPIsQuery,
  useLazyGetKPIsQuery,
  useAddKpiMutation,
  useAddCategoryMutation,
  useDeleteCategoryMutation,
  useUpdateCategoryMutation,
  useMakeCategoryParentMutation,
  useAddRepairServiceMutation,
  useDeleteRepairServiceMutation,
  useUpdateRepairServiceMutation,
  useOxAppReleaseQuery,
  useLazyGetRepairServicesQuery,
  useGetRepairServicesQuery,
  useGetShiftPreferencesQuery,
  useAddShiftPreferencesMutation,
  useUpdateRepairLogMutation,
  useUpdatePricingPreferenceMutation,
  useGetPricingPreferenceQuery,
  useGetPaymentPreferencesQuery,
  useAddPaymentPreferencesMutation,
  useAddVehicleCategoryMutation,
  useGetVehicleCategoryQuery,
  useEditVehicleCategoryMutation,
  useGetCustomInspectionsQuery,
  useAddCustomInspectionMutation,
  useDeleteCustomInspectionMutation,
  useEditCustomInspectionMutation,
  useGetAlertsQuery,
  useLazyGetAlertsQuery,
  useChangeAlertStatusMutation,
  useCloseShiftViolationMutation
} = settingsApi;
